import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, viewChildren } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { widgetEditMode, IWidgetReturnTranslatedValues, IDragFileBrowseData, decodePlaceholderUrls, removeUndefined, checkIfFieldDisabled, getWidgetFormatedData, IReturnChangesWidgetEditForm } from 'na-lib';
import { CommentsService } from 'src/app/service/comments.service';
import { SettingsService } from 'src/app/service/settings.service';
import { UploadService } from 'src/app/service/upload.service';
import { urlInvalidCharactersValidator } from 'src/app/validators/formValidators.dt';
import MarkdownIt from 'markdown-it';
import TurndownService from 'turndown';
import Quill from 'quill';
import { supportUrlStructure } from 'src/app/supportUrls';
import { Subject, Subscription, takeUntil } from 'rxjs';
import {MatMenu, MatMenuTrigger} from "@angular/material/menu";
import {QuillEditorComponent} from "ngx-quill";
import {QuillDeltaToHtmlConverter} from "quill-delta-to-html";

type LinkChip =
  | { kind: 'child'; id: string; label: string }
  | { kind: 'anchor'; id: string; label: string };

interface AnchorOption {
  id: string;
  label?: string;
  name?: string;
}
interface ChildPageOption {
  id: string;
  name?: string;
}
interface SchemaMenuNode {
  key: string;
  path: string;
  children: SchemaMenuNode[];
}
interface PayloadTemplateMenuNode {
  title: string;
  keys: string[];
}

@Component({
    selector: 'app-text-edit-form',
    templateUrl: './text-edit-form.component.html',
    styleUrl: './text-edit-form.component.css',
    standalone: false
})

export class TextEditFormComponent {
  @Input() data:widgetEditMode;//widgetTranslationModeData
  @Output() returnChanges = new EventEmitter<IReturnChangesWidgetEditForm>();
  @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;
  @ViewChild(QuillEditorComponent, { static: true }) editor!: QuillEditorComponent;
  @Output() formChange = new EventEmitter<FormGroup>();

  userHasBetaWidgetRole = false
  defaultLanguage:string
  oldData:any
  textData:any
  widgetNumber:any
  passId:any
  url:any
  disableFilters = false
  translationModeOriginal = false
  dragFileData: IDragFileBrowseData
  textEditForm!: FormGroup
  changes=false
  addUrl = false
  urlData:any
  dynamicUrl = false
  newImage:any=[]
  linkUrlTransform:any
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
  urlRegex = /^(?:\$\{[A-Za-z0-9._:-]+\}|#\S+|child:[^\s]+|dpp:\/\/main|(?:https?:\/\/|context:\/\/|pass:\/\/|content:\/\/|task:\/\/|tridy:\/\/)\S+)$/;
  urlRegexDoc = /^(?:\$\{[^}]+\}|pass:\/\/[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+)$/;
  urlRegexDynamic = /^\$\{[A-Za-z0-9._:-]+\}$/;

  payloadKeys: string[] = [];
  payloadTemplateMenuNodes: PayloadTemplateMenuNode[] = [];
  schemaKeys: string[] = [];
  schemaMenuNodes: SchemaMenuNode[] = [];
  schemaBranchNodes: SchemaMenuNode[] = [];
  readonly schemaBranchMenus = viewChildren<MatMenu>('schemaBranchMenu');
  workTaskTemplatesList = []
  isDefaultLeft:boolean

  anchorOptions: AnchorOption[] = [];
  childPages: ChildPageOption[] = [];
  linkChip: LinkChip | null = null;

  quillModules = {
    toolbar: {
      container: '#toolbar',
    },
  };

  private md: MarkdownIt;
  private turndownService: TurndownService;
  private langChangeSub: Subscription;
  languageClass: string = '';
  private destroy$ = new Subject<void>();

  constructor(public dialog: MatDialog,
    private uploadService: UploadService,
    private fb: FormBuilder,
    private commentService: CommentsService,
    private settingsService: SettingsService,
    private translate:TranslateService){
      this.md = new MarkdownIt({ breaks: true });
      this.turndownService = new TurndownService();

      // Preserve line breaks
      this.turndownService.addRule('lineBreaks', {
        filter: 'br',
        replacement: function () {
          return '\n';
        }
      });

      this.turndownService.addRule('paragraphAsLines', {
        filter: 'p',
        replacement: function (content, node: any) {
          const rawHtml = (node.innerHTML || '').trim();
          // Paragraph that is only <br> / whitespace => blank line
          const onlyBrOrEmpty = rawHtml.replace(/<br\s*\/?>/gi, '').trim() === '';
          if (onlyBrOrEmpty) // Blank paragraph = one completely empty line
            return '\n\n';
          // Normal paragraph: add a single newline at the end
          return content + '\n';
        }
      });

      // Override Quill's Link sanitizer to allow child: and # protocols
      const Link = Quill.import('formats/link') as any;
      const builtInSanitize = Link.sanitize;
      Link.sanitize = function(url: string) {
        if (url && (url.startsWith('child:') || url.startsWith('#') || url.startsWith('dpp://'))) {
          return url;
        }
        return builtInSanitize.call(this, url);
      };

}

  ngOnInit():void{
    if(this.settingsService.checkIfBetaRoleAccess())
      this.userHasBetaWidgetRole = true

    this.defaultLanguage = this.data.defaultLanguage
    this.textData=this.data.values
    this.oldData=this.textData
    this.url=this.oldData.url
    this.passId = this.data.passId
    this.widgetNumber= this.data.widget

    if(this.data.translateMode){
      this.disableFilters = true
      let translateData = this.data.translateData
      const {fields, originalLanguageData, defaultLanguageWidgetData, valuesTranslated, selectedLanguage, values} = translateData

      if(translateData.originalLanguageData){
        this.translationModeOriginal = true
        this.dragFileData = translateData.dragFileDataOriginal
        this.initTranslateEditData( defaultLanguageWidgetData, valuesTranslated, originalLanguageData, [],selectedLanguage)
      }else{
        this.translationModeOriginal = false

        this.dragFileData = translateData.dragFileData
        this.initTranslateEditData(values,valuesTranslated, originalLanguageData, fields, selectedLanguage)
      }
    }
    else{
      this.dragFileData = {
        calledFrom: TextEditFormComponent,
        calledFromData:{
          passId: this.passId,
          widgetNumber:this.widgetNumber,
          widget: "text",
        },
        enableDragDrop: true,
        enableBrowse:true,
        enableMedia:true,
        enableEnterUrl:true,
        enableMultipleDragDrop:false,
        supportedFileTypes:['image/jpeg', 'image/png', 'image/webp'],
        supportedFileAccept:".jpg, .jpeg, .png, .webp",
        maxSizeInBytes: 2 * 1024 * 1024, // 2 MB size limit for compression,
        maxAllowedSizeInBytes:20 * 1024 * 1024, // 20 MB size limit for direct upload,
        returnSupportUrl: undefined,
        returnUrlData: undefined,
        enterUrl:false,
        returnUrlType:undefined
      }
      this.initWidgetEdit()
    }

    // check for payload keys and add them
    this.fetchAndInspect()

    // Set initial class based on current language
    this.languageClass = this.getLanguageClass();
    // Subscribe to language change events
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.languageClass = this.getLanguageClass();
    });

    const extra = this.data as any;
    this.anchorOptions = extra.anchorOptions || [];
    this.childPages   = extra.childPages || [];

    const ctrl = this.textEditForm?.get('imageLinkURL');
    if (ctrl) {
      this.linkChip = this.makeLinkChip(ctrl.value);
      ctrl.valueChanges.subscribe(v => {
        this.linkChip = this.makeLinkChip(v);
      });
    }

  }

  initTranslateEditData(data, valuesTranslated, originalLanguageData, fields, selectedLanguage){

    const { id,filter, type, image, imagePosition,widgetStyle, name, textAlignment } = data
    let { title, subtitle, text } = data
    let { altTitle } = image ?? {}

    if(!originalLanguageData){
      title = valuesTranslated[selectedLanguage +'.'+id+'.title']
      subtitle = valuesTranslated[selectedLanguage +'.'+id+'.subtitle']
      text = valuesTranslated[selectedLanguage +'.'+id+'.text']
      altTitle = valuesTranslated[selectedLanguage +'.'+id+'_image.altTitle']
    }

    if(image?.url){
      this.checkdyanimcContentUrl(image.url)
      this.supportUrl(image.url)
    }

    this.textEditForm = this.fb.group({
      id:[{value:id ? id : undefined,  disabled: checkIfFieldDisabled('id', fields, originalLanguageData)}],
      type:[{value: type ? type : undefined,  disabled: checkIfFieldDisabled('type', fields, originalLanguageData)}],
      title: [{value:title ? title : undefined,  disabled: checkIfFieldDisabled('title', fields, originalLanguageData)}],
      subtitle: [{value:subtitle ? subtitle : undefined,  disabled: checkIfFieldDisabled('subtitle', fields, originalLanguageData)}],
      text:[{value:text ? text : undefined,  disabled: checkIfFieldDisabled('text', fields, originalLanguageData)}],
      textToSave:[{value: text ? text : undefined, disabled:checkIfFieldDisabled('text', fields, originalLanguageData)}],
      image:[{value:image ? image : undefined,  disabled: checkIfFieldDisabled('image', fields, originalLanguageData)}],
      imageUrl:[ {value:image?.url ? image?.url : undefined,  disabled: checkIfFieldDisabled('imageUrl', fields, originalLanguageData)} ,[Validators.pattern(this.urlRegex), urlInvalidCharactersValidator()]],
      imageLinkURL:[{value: image?.linkURL ? image?.linkURL : undefined, disabled: checkIfFieldDisabled('imageLinkURL', fields, originalLanguageData)}  ,[Validators.pattern(this.urlRegex), urlInvalidCharactersValidator()]],
      imageSize:[{value: image?.size ? image?.size : undefined, disabled: checkIfFieldDisabled('imageSize', fields, originalLanguageData)}  ],
      // imageDescription:[],
      imagePosition :[{value: imagePosition ? imagePosition : undefined, disabled: checkIfFieldDisabled('imagePosition', fields, originalLanguageData)}],
      name :[{value: name ? name : undefined, disabled: checkIfFieldDisabled('name', fields, originalLanguageData)}],
      textAlignment :[{value: textAlignment ? textAlignment : undefined, disabled: checkIfFieldDisabled('textAlignment', fields, originalLanguageData)}],
      filter: [{value:filter ? filter: undefined,  disabled: checkIfFieldDisabled('filter', fields, originalLanguageData)}],
      widgetStyle:[{value: widgetStyle ? widgetStyle : undefined, disabled: checkIfFieldDisabled('widgetStyle', fields, originalLanguageData)}],
      altTitle:[ {value: altTitle ? altTitle : undefined,  disabled: checkIfFieldDisabled('image.altTitle', fields, originalLanguageData)}],
      imageFallbackUrl:[{value: image?.fallbackUrl ? image.fallbackUrl : undefined, disabled: checkIfFieldDisabled('image.fallbackUrl', fields, originalLanguageData)}, [Validators.pattern(this.urlRegex), urlInvalidCharactersValidator()]]
    })

    // Load existing markdown
    if (text) {
      this.loadContent(text); // Load markdown into editor
    }

    this.textEditForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe( value => {
      if(value.imageUrl){
        this.checkdyanimcContentUrl(value.imageUrl)
        if(value?.textAlignment=='center')
          this.textEditForm.patchValue({textAlignment : 'left'})
      }

      const data = getWidgetFormatedData({
        ...value,
        textToSave: this.cleanPlaceHolderStyles(value?.textToSave)
      }, this.isDefaultLeft);

      const {title, subtitle, image, text} = data
      this.returnChanges.emit({
        type:"text",
        form: this.textEditForm,
        validForm: this.textEditForm.valid,
        translations:{title, subtitle, text, altTitle: image?.altTitle ? image.altTitle : undefined},
        saveDisabled: false
      })
    })

  }

  checkDisabledField(field:string){
    return checkIfFieldDisabled(field, this.data?.translateData?.fields ?? [{key:'imageUrl'}, {key:'imageLinkURL'}], this.data?.translateData?.originalLanguageData ?? false)
  }

  initWidgetEdit(){
    const {id,filter, type, title, subtitle, text, image, imagePosition,widgetStyle, name, textAlignment} = this.textData

    if(image?.url){
      this.checkdyanimcContentUrl(image.url)
      this.supportUrl(image.url)
    }

    this.textEditForm = this.fb.group({
      id:[{value:id ? id : undefined, disabled:false}],
      type:[{value:type ? type : undefined, disabled:false}],
      title:[{value: title ? title : undefined, disabled:false}],
      subtitle:[{value: subtitle ? subtitle : undefined, disabled:false}],
      text:[{value: text ? text : undefined, disabled:false}],
      textToSave:[{value: text ? text : undefined, disabled:false}],
      image:[{value: image ? image : undefined, disabled:false}],
      imageUrl:[ {value: image?.url ? image?.url : undefined, disabled:false} ,[Validators.pattern(this.urlRegex), urlInvalidCharactersValidator()]],
      imageLinkURL:[{value: image?.linkURL ? image?.linkURL : undefined, disabled:false}  ,[Validators.pattern(this.urlRegex), urlInvalidCharactersValidator()]],
      imageSize:[{value: image?.size ? image?.size : undefined, disabled:false} ],
      // imageDescription:[],
      imagePosition:[{value: imagePosition ? imagePosition : undefined, disabled:false} ],
      name:[{value: name ? name : undefined, disabled:false}],
      textAlignment:[{value:  textAlignment ? textAlignment : undefined, disabled:false} ],
      filter:[{value: filter ? filter :undefined, disabled:false}],
      widgetStyle:[{value: widgetStyle ? widgetStyle : undefined, disabled:false}],
      altTitle:[{value: image?.altTitle ? image?.altTitle : undefined, disabled:false}],
      imageFallbackUrl:[{value: image?.fallbackUrl ? image?.fallbackUrl: undefined, disabled:false} , [Validators.pattern(this.urlRegex), urlInvalidCharactersValidator()]]
    })

    if(text)
    this.loadContent(text); // Load markdown into editor

    this.checkdyanimcContentUrl(this.textEditForm.value.imageUrl)

    if (!textAlignment) {
      this.textEditForm.get('textAlignment')!.setValue('left', { emitEvent: false });
      this.isDefaultLeft = true; // <-- track that it's just a visual default
    }

    this.textEditForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe( value => {
      if(value.imageUrl){
        this.checkdyanimcContentUrl(value.imageUrl)
        if(value?.textAlignment=='center')
          this.textEditForm.patchValue({textAlignment : 'left'})
      }
      const cleanedText = this.cleanPlaceHolderStyles(value?.textToSave)
      const data = getWidgetFormatedData({ ...value, textToSave: cleanedText }, this.isDefaultLeft);

      this.returnChanges.emit({
        type:"text",
        form: this.textEditForm,
        validForm: this.textEditForm.valid,
        data,
        saveDisabled: !this.textEditForm.valid
      })

    })

    this.textEditForm.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.formChange.emit(this.textEditForm);
    });

    this.textEditForm.get('image')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe( (value:any) =>{
      if(value && value.url){
        this.supportUrl(value.image.url)
        if(this.textEditForm.value.textAlignment && this.textEditForm.value.textAlignment=='center')
          this.textEditForm.patchValue({textAlignment : 'left'})
      }
    })

    this.textEditForm.get('imageLinkURL')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe( (value:any) =>{
      this.setFieldValidator('imageLinkURL', value)
    })

    this.textEditForm.get('imageFallbackUrl')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe( (value:any) =>{
      this.setFieldValidator('imageFallbackUrl', value)
    })

    this.textEditForm.get('imageUrl')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe( (value:any) =>{
      this.checkdyanimcContentUrl(value)
      this.setFieldValidator('imageUrl', value)

      if(this.addUrl && !this.dynamicUrl){
        this.url = value
        if ( value.includes('https://') || value.includes('http://') || value.includes('pass://') || value.includes('tridy://') || value.includes('context://') )
        this.supportUrl(value)
      }

      if(value && !this.dynamicUrl)
        this.calculateImageSize()

      if(value ){
        if(this.textEditForm.value.textAlignment && this.textEditForm.value.textAlignment=='center')
          this.textEditForm.patchValue({textAlignment : 'left'})
      }
    })

  }

  cleanPlaceHolderStyles(textToSave: string | undefined): string {
    if (!textToSave) return textToSave ?? '';

    return textToSave.replace(/\$\{([^{}]*)\}/g, (_, expression) => {
      // Quill/Turndown can place inline Markdown markers inside a placeholder
      // when a key is pasted into formatted text. Keep the surrounding style,
      // but persist only the actual key inside ${...}.
      const key = expression.replace(/\\?[*`~]/g, '');
      return '${' + key + '}';
    });
  }

  setFieldValidator(field, value) {
    if (!value) return;

    if (value.match(/^\$\{[A-Za-z0-9._:-]+\}$/)){
      this.textEditForm.get(field)?.setValidators([ Validators.pattern(this.urlRegexDynamic)]);
      this.textEditForm.get(field)?.updateValueAndValidity({ emitEvent: false });
    } else {
      this.checkPattern_url(field)
    }
  }

  buildAnchorUrl(anchor: { id: string }): string {
    if (!anchor || !anchor.id) {
      console.warn('buildAnchorUrl: invalid anchor', anchor);
      return '';
    }
    return `#${anchor.id}`;
  }

  buildChildPageUrl(child: { id: string }): string {
    if (!child || !child.id) {
      console.warn('buildChildPageUrl: invalid child', child);
      return '';
    }
    return child.id === 'main' ? 'dpp://main' : `child:${child.id}`;
  }

  setLinkUrl(value: string): void {
    const controlName = 'imageLinkURL';
    this.textEditForm.patchValue({
      [controlName]: value
    });

    const ctrl = this.textEditForm.get(controlName);
    if (ctrl) {
      ctrl.markAsDirty();
      ctrl.markAsTouched();
      ctrl.updateValueAndValidity();
    }
  }

  private makeLinkChip(value: string | null | undefined): LinkChip | null {
    if (!value) return null;
    // dpp://main
    if (value === 'dpp://main') {
      const match = this.childPages?.find(x => x.id === 'main');
      return { kind: 'child', id: 'main', label: match?.name || 'Main' };
    }
    // child:<id>
    if (value.startsWith('child:')) {
      const id = value.slice('child:'.length).trim();
      if (!id) return null;
      const match = this.childPages?.find(x => x.id === id);
      return {
        kind: 'child',
        id,
        label: match?.name || id,
      };
    }
    // #<id>
    if (value.startsWith('#')) {
      const id = value.slice(1).trim();
      if (!id) return null;
      const match = this.anchorOptions?.find(x => x.id === id);
      return {
        kind: 'anchor',
        id,
        label: match?.label || match?.name || id,
      };
    }
    // normal url -> no chip
    return null;
  }

  clearLinkChip(): void {
    this.setLinkUrl('');
  }

  checkDynamicValuesUnderscore(data){
    let result = [...data.matchAll(/\${(.+)\}/g)];
    let newdata = data
    if(result){
      const value = `\\\\_`
      newdata = newdata.replaceAll(value, '_')

      const value2 = `\\_`
      newdata = newdata.replaceAll(value2, '_')

      const value1 = `\_`
      newdata = newdata.replaceAll(value1,'_')
      data = newdata
    }
    return data
  }

  loadTextValueFromMarkdown(){
    const markdownContent = this.saveMarkdown();
    const cleanedAll = this.cleanPlaceHolderStyles(this.checkDynamicValuesUnderscore(markdownContent))
    this.textEditForm.get('textToSave')?.setValue(cleanedAll);
  }

  ngOnDestroy() {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  async calculateImageSize(){
    const imageSize = this.textEditForm.get('imageSize')?.value
    const imageUrl = this.textEditForm.get('imageUrl')?.value

    if(imageSize?.length == 0 || (imageSize && imageSize[0] == 0 && imageSize[1] == 0) || !imageSize ){
      const img = supportUrlStructure(imageUrl, this.passId, this.settingsService.contextId$)

      await this.uploadService.calculateImgFileSize(img).then(value => {
        this.textEditForm.patchValue({imageSize: value})
      }).catch( error => {
        console.log (error)
      })
    }
  }

  checkPattern_url(field){
    const fiedlValue:any = this.textEditForm.get(field)
    if(fiedlValue){
      try{
        const imgUrl = new URL(fiedlValue)

        if(imgUrl.protocol === "http:" || imgUrl.protocol === "https:" || imgUrl.protocol === "context:" || imgUrl.protocol === "tridy:")
        this.textEditForm.get(field)?.setValidators([Validators.pattern(this.urlRegex), urlInvalidCharactersValidator()]);

        if(imgUrl.protocol === "pass:" )
          this.textEditForm.get(field)?.setValidators([Validators.pattern(this.urlRegexDoc), urlInvalidCharactersValidator()]);

        this.textEditForm.updateValueAndValidity()
      }catch(e){}
    }
  }

  checkdyanimcContentUrl(value){
    if(value?.match(/\${(.+)\}/))
    this.dynamicUrl = true
    else
    this.dynamicUrl = false
  }

  getLanguageClass(): string {
    const currentLang = this.translate.currentLang() || this.translate.fallbackLang() || 'en';
    return `lang-${currentLang}`;
  }

  readDocument(event: any): void {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const maxSizeInBytes = 10 * 1024 * 1024; // 10 MB size limit

      // Define allowed file types
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/webp',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'text/csv',
        'text/vcard',
        'application/rtf'
      ];

      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        const message = this.translate.instant("SnackBarConstants.INVALID_FILE_FORMAT");
        this.commentService.addSnackBar.emit(message);
        return;
      }

      // Validate file size
      if (file.size > maxSizeInBytes) {
        const message = this.translate.instant("SnackBarConstants.FILE_TOO_LARGE");
        this.commentService.addSnackBar.emit(message);
        return;
      }

      // Proceed with reading and uploading the file
      const reader = new FileReader();
      this.newImage = file;
      reader.readAsDataURL(file);
      reader.onload = async (e) => {
        if (e.target) {
          const urlNew = e.target.result as string;

          try {
            const uploadURL = await this.uploadService.uploadDocument(this.passId, this.newImage, this.widgetNumber);
            if (uploadURL) {
              this.textEditForm.get("imageLinkURL")?.setValidators([Validators.pattern(this.urlRegexDoc), urlInvalidCharactersValidator()]);
              this.linkUrlTransform = uploadURL;
              this.textEditForm.patchValue({ imageLinkURL: this.linkUrlTransform });
            }
          } catch (error) {
            console.error(error);
            const message = this.translate.instant("SnackBarConstants.UPLOAD_IMG_FAILED");
            this.commentService.addSnackBar.emit(message);
          }
        }
      };
    }
  }


  deleteImg(x:any){
    this.url=undefined
    this.textEditForm.patchValue({ image:undefined, imageUrl: undefined, imageLinkURL: undefined, imageSize: undefined, altTitle:undefined , imageFallbackUrl: undefined})
    this.urlData=""
    this.dragFileData.enterUrl = false
  }

  supportUrl(url){
    this.urlData = url
    if(url){
      try{
        const firebaseUrl = supportUrlStructure(url, this.passId, this.settingsService.contextId$)
        this.urlData=firebaseUrl
      }catch(error){}
    }
  }

  checkNewUrl(){
    this.url = this.textEditForm.value.imageUrl
    this.supportUrl(this.textEditForm.value.imageUrl)
  }

  typeURL(){
    this.addUrl = true
    this.urlData = './../../assets/img/default.jpg';
  }

//-----------------------------
  returnChangesFilters($event){
    this.textEditForm.patchValue({filter: $event.newFilters})
  }

  returnChangesStyle($event){
    this.textEditForm.patchValue({widgetStyle: $event.newStyle})
  }

  returnChangesDragFileBrowseUI($event){
    if($event){
      const {calledFrom, calledFromData, enableDragDrop, enableBrowse, enableMedia,enableEnterUrl, enableMultipleDragDrop,
        supportedFileTypes,supportedFileAccept, maxSizeInBytes,maxAllowedSizeInBytes,
        returnSupportUrl,returnUrlData, enterUrl} = $event

        this.dragFileData = $event
      if(enterUrl){
        this.addUrl = true
        this.urlData = './../../assets/img/default.jpg'
      }else{
        this.addUrl = false
        this.urlData = undefined
      }

      if(returnUrlData)
      this.urlData = returnUrlData

      if(returnSupportUrl){
        this.url = returnSupportUrl
        this.textEditForm.patchValue({ imageUrl: returnSupportUrl })
      }
    }
  }

  // --------------------------
  // START WYSWIG EDITOR


  fetchAndInspect() {
    this.getSchemaKeysList();
    this.getWorkTasksList();
  }

  inspectWorkTaskTemplatesList() {
    if (Array.isArray(this.workTaskTemplatesList)) {
      const payloadKeys = new Set<string>();
      const payloadTemplateMenuNodes: PayloadTemplateMenuNode[] = [];

      this.workTaskTemplatesList.forEach((workTask) => {
        const templateKeys = new Set<string>();
        workTask.formSections?.forEach((section) => {
          section.fields?.forEach((field) => {
            if (field.payloadKey) {
              payloadKeys.add(field.payloadKey);
              templateKeys.add(field.payloadKey);
            }
          });
        });
        if (templateKeys.size > 0) {
          payloadTemplateMenuNodes.push({
            title: workTask.title || workTask.name || workTask.id || 'Vorlage',
            keys: Array.from(templateKeys)
          });
        }
      });

      this.payloadKeys = Array.from(payloadKeys);
      this.payloadTemplateMenuNodes = payloadTemplateMenuNodes;
    } else {
      this.payloadKeys = [];
      this.payloadTemplateMenuNodes = [];
    }
  }


  getWorkTasksList() {
    const contentTypeId = this.getContentTypeIdForPayloadKeys();

    if(!contentTypeId){
      this.workTaskTemplatesList = this.settingsService.settingsWorkTaskTemplates ?? [];
      this.inspectWorkTaskTemplatesList();
      return;
    }

    try{
      this.commentService.progressSpin.emit(true)
      this.settingsService.observeContextsWorkTasksTemplatesFirestoreWithContentTypeId(contentTypeId).pipe(takeUntil(this.destroy$)).subscribe({
        next: (val) => {
          this.workTaskTemplatesList = val
          this.inspectWorkTaskTemplatesList();
          this.commentService.progressSpin.emit(false)
        },
        error: () => {
          this.commentService.progressSpin.emit(false)
          const message = this.translate.instant("SnackBarConstants.LOAD_FAILED")
          this.commentService.addSnackBar.emit(message)
        }
      })

    }catch(error){
      this.commentService.progressSpin.emit(false)
      const message = this.translate.instant("SnackBarConstants.LOAD_FAILED")
      this.commentService.addSnackBar.emit(message)
    }
  }

  private getContentTypeIdForPayloadKeys(): string | undefined {
    const data = this.data as any;
    return data?.contentTypeId
      || data?.translateData?.contentTypeId
      || data?.values?.contentTypeId
      || data?.values?.contentType;
  }

  async getSchemaKeysList() {
    const data = this.data as any;
    const schemaFromDialog = data?.contentTypeSchema || data?.values?.schema;

    if (schemaFromDialog) {
      this.schemaKeys = this.extractSchemaKeys(schemaFromDialog);
      this.setSchemaMenuNodes(schemaFromDialog);
      return;
    }

    const contentTypeId = this.getContentTypeIdForPayloadKeys();
    if (!contentTypeId) {
      this.schemaKeys = [];
      this.schemaMenuNodes = [];
      this.schemaBranchNodes = [];
      return;
    }

    try {
      const contentType = await this.settingsService.observeContextsContentTypesIdFirestore(contentTypeId);
      this.schemaKeys = this.extractSchemaKeys(contentType?.['schema']);
      this.setSchemaMenuNodes(contentType?.['schema']);
    } catch {
      this.schemaKeys = [];
      this.schemaMenuNodes = [];
      this.schemaBranchNodes = [];
    }
  }

  private setSchemaMenuNodes(schema: any): void {
    this.schemaMenuNodes = this.extractSchemaMenuNodes(schema);
    const branches: SchemaMenuNode[] = [];
    const collect = (nodes: SchemaMenuNode[]) => {
      for (const node of nodes) {
        if (node.children.length) {
          branches.push(node);
          collect(node.children);
        }
      }
    };
    collect(this.schemaMenuNodes);
    this.schemaBranchNodes = branches;
  }

  schemaBranchMenuFor(node: SchemaMenuNode): MatMenu | null {
    const index = this.schemaBranchNodes.indexOf(node);
    return index < 0 ? null : this.schemaBranchMenus()[index] ?? null;
  }

  private extractSchemaKeys(schema: any): string[] {
    const properties = Array.isArray(schema) ? schema : schema?.properties;
    const keys = new Set<string>();
    this.collectSchemaKeys(properties || [], '', keys);
    return Array.from(keys);
  }

  private collectSchemaKeys(nodes: any[], parent: string, result: Set<string>): void {
    if (!Array.isArray(nodes)) return;

    nodes.forEach((node) => {
      if (!node?.key) return;

      const key = parent ? `${parent}.${node.key}` : node.key;
      result.add(key);

      const childProperties = node.properties || node.items?.properties;
      if (Array.isArray(childProperties) && childProperties.length) {
        this.collectSchemaKeys(childProperties, key, result);
      }
    });
  }

  private extractSchemaMenuNodes(schema: any): SchemaMenuNode[] {
    const properties = Array.isArray(schema) ? schema : schema?.properties;
    return this.buildSchemaMenuNodes(properties || [], '');
  }

  private buildSchemaMenuNodes(nodes: any[], parent: string): SchemaMenuNode[] {
    if (!Array.isArray(nodes)) return [];

    return nodes
      .filter(node => !!node?.key)
      .map((node) => {
        const path = parent ? `${parent}.${node.key}` : node.key;
        const childProperties = node.properties || node.items?.properties;
        const children = this.buildSchemaMenuNodes(childProperties || [], path);

        return {
          key: node.key,
          path,
          children
        };
      });
  }


  loadContent(markdown: string) {
    this.markdownToQuill(markdown);
  }

  private quillEditor: any;
  onEditorCreated(quill: any) {
    this.quillEditor = quill;

    const keyboard = quill.getModule('keyboard');
    keyboard.bindings[13] = [];
    keyboard.addBinding({ key: 13 }, {
      handler: (range, context) => {
        quill.insertText(range.index, '\n');
        quill.setSelection(range.index + 1, Quill.sources.SILENT);
      }
    });


    // OVERRIDE BOLD AND ITALIC SO THEY ARE EXCLUSIVE

    const toolbar = quill.getModule('toolbar');
    if (toolbar) {
      toolbar.addHandler('bold', () => {
        this.toggleExclusiveFormat(quill, 'bold');
      });
      toolbar.addHandler('italic', () => {
        this.toggleExclusiveFormat(quill, 'italic');
      });
    }

    quill.root.addEventListener(
      'keydown',
      (event: KeyboardEvent) => {
        const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
        const shortKey = isMac ? event.metaKey : event.ctrlKey;

        if (!shortKey) {
          return;
        }

        // Normalise key
        const key = event.key.toLowerCase();

        if (key === 'b') {
          event.preventDefault();
          event.stopPropagation();
          this.toggleExclusiveFormat(quill, 'bold');
        } else if (key === 'i') {
          event.preventDefault();
          event.stopPropagation();
          this.toggleExclusiveFormat(quill, 'italic');
        }
      },
      true
    );

    if (this.textEditForm.value.text) {
      this.markdownToQuill(this.textEditForm.value.text);
    }

    // Listen to editor changes
    quill.on('text-change', (delta, oldDelta, source) => {
      if (source === 'user') {
        this.loadTextValueFromMarkdown();
      }
    });

    quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node: any, delta: any) => {
      const retainStyles = ['bold', 'italic', 'link', 'list'];
      delta.ops.forEach((op: any) => {
        if (op.attributes) {
          Object.keys(op.attributes).forEach((attr) => {
            if (!retainStyles.includes(attr)) {
              delete op.attributes[attr];
            }
          });
        }
      });
      return delta;
    });
  }
  private isInsertingContent: boolean = false;

  markdownToQuill(markdown: string) {
    if (!this.editor || !this.editor.quillEditor) return;
    if (this.isInsertingContent) return;

    this.isInsertingContent = true;

    // Normalize CRLF
    const normalized = markdown.replace(/\r\n/g, '\n');

    const lines = normalized.split('\n');
    const htmlParts: string[] = [];

    // Lines that start a markdown list:
    //  - "- item", "* item", "+ item"
    //  - "1. item", "2. item", etc.
    const listLineRegex = /^\s*(?:[*+-]|\d+\.)\s+/;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Empty line = explicit blank line
      if (line.trim() === '') {
        htmlParts.push('<br>');
        continue;
      }

      // LIST
      if (listLineRegex.test(line)) {
        const listLines: string[] = [];
        let j = i;

        while (j < lines.length && listLineRegex.test(lines[j])) {
          listLines.push(lines[j]);
          j++;
        }

        const listBlock = listLines.join('\n');

        let listHtml = this.md.render(listBlock);
        listHtml = decodePlaceholderUrls(listHtml);

        htmlParts.push(listHtml);

        i = j - 1;
        continue;
      }

      // INLINE
      let rendered = this.md.renderInline(line);
      rendered = decodePlaceholderUrls(rendered);

      htmlParts.push(rendered);
      htmlParts.push('<br>');
    }

    let html = htmlParts.join('');
    html = decodePlaceholderUrls(html);

    this.editor.quillEditor.clipboard.dangerouslyPasteHTML(html);
    this.isInsertingContent = false;
  }

  saveMarkdown() {
    if (!this.editor || !this.editor.quillEditor) return '';

    const delta = this.editor.quillEditor.getContents();
    const conv = new QuillDeltaToHtmlConverter(delta.ops, {
      multiLineParagraph: false,   // keeps paragraphs tidy
    });
    const htmlFromDelta = conv.convert();

    //sanitize lists / strip empties:
    const doc = new DOMParser().parseFromString(htmlFromDelta, 'text/html');

    // remove empty <li> like <li><br></li>
    doc.querySelectorAll('li').forEach(li => {
      const text = (li.textContent || '').replace(/[\s\u200B\uFEFF]+/g, '');
      const onlyBr = li.innerHTML.replace(/<br\s*\/?>/gi, '').trim() === '';
      if (!text || onlyBr) li.remove();
    });

    // (optional) tidy up stray <br> after lists
    doc.querySelectorAll('ul,ol').forEach(list => {
      while (list.firstElementChild && (list.firstElementChild as HTMLElement).innerHTML.replace(/<br\s*\/?>/gi,'').trim() === '') {
        list.firstElementChild.remove();
      }
      while (list.lastElementChild && (list.lastElementChild as HTMLElement).innerHTML.replace(/<br\s*\/?>/gi,'').trim() === '') {
        list.lastElementChild.remove();
      }
    });

    let sanitizedHtml = doc.body.innerHTML;
    sanitizedHtml = sanitizedHtml
      // ${...}
      .replace(/href="unsafe:(\$\{[^}]+\})"/g, 'href="$1"')
      // #anchors
      .replace(/href="unsafe:(#[^"]+)"/g, 'href="$1"')
      // child: inside href=""
      .replace(/href="unsafe:(child:[^"]+)"/g, 'href="$1"')
      // dpp:// inside href=""
      .replace(/href="unsafe:(dpp:\/\/[^"]+)"/g, 'href="$1"')
      // fallback: any remaining unsafe:child: anywhere
      .replace(/unsafe:child:/g, 'child:')
      // fallback: any remaining unsafe:dpp:// anywhere
      .replace(/unsafe:dpp:\/\//g, 'dpp://');

    const markdownContent = this.turndownService.turndown(sanitizedHtml);
    return markdownContent;
  }

  addPayloadKeyAtCursor(payloadKey: string) {
    const quillEditor = this.editor.quillEditor;
    const range = quillEditor.getSelection(true);
    if (range) {
      const formattedPayloadKey = `\${${payloadKey}}`;
      setTimeout(() => {
        quillEditor.deleteText(range.index, range.length);
        quillEditor.insertText(range.index, formattedPayloadKey);
        this.loadTextValueFromMarkdown()
      }, 0);
    }
  }

  applyEditorLink(url: string): void {
    if (!this.editor || !this.editor.quillEditor) {
      return;
    }
    const quill = this.editor.quillEditor;
    const range = quill.getSelection(true);
    if (!range) {
      return;
    }
    if (range.length === 0) {
      quill.insertText(range.index, url, { link: url });
      quill.setSelection(range.index + url.length, 0);
    } else {
      quill.formatText(range.index, range.length, 'link', url);
    }
    this.loadTextValueFromMarkdown();
  }

  private toggleExclusiveFormat(quill: any, format: 'bold' | 'italic') {
    const range = quill.getSelection();
    if (!range) {
      return;
    }
    const currentFormat = quill.getFormat(range);
    const shouldEnable = !currentFormat[format];

    quill.format('bold', false);
    quill.format('italic', false);

    if (shouldEnable) {
      quill.format(format, true);
    }
    // Manually sync markdown -> form.
    this.loadTextValueFromMarkdown();
  }
}
